import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalyticsDebugger } from '@features/Analytics/hooks/useAnalytics';
import { useTranslationStyles } from '@features/Analytics/hooks/useTranslationStyles';
import { useFeatures } from '@features/FeatureToggle';
import { LANG_TO_LOCALE } from '@features/intl/contants';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { UiKitButton } from 'src/components/common/ui-kit-button';
import { ALL_VARIANTS } from 'src/constants/variants';
import { groupBy } from 'src/utils';
import styles from './debug-ui.module.css';
type TpSource = 'PT' | 'GA' | 'FB' | 'bing' | 'SNAP' | 'TikTok' | 'AppLovin';
const DEBUG_EVENT_SOURCES: TpSource[] = ['GA', 'FB', 'TikTok', 'PT', 'SNAP', 'bing', 'AppLovin'];
type Event = {
  name: string;
  date: string;
  params?: Record<string, any>;
};
export const DebugUi = observer(() => {
  const [activeTab, setActiveTab] = useState<TpSource>('GA');
  const {
    authStore: {
      variant: variantFromStorage
    },
    analyticsStore: {
      abTests
    }
  } = useStores();
  const features = useFeatures();
  const {
    events,
    clearEvents,
    eventsNumberVisible,
    setEventsNumberVisible
  } = useAnalyticsDebugger();
  const [activeVariant, setActiveVariant] = useState(variantFromStorage);
  const [activeLanguage, setActiveLanguage] = useState(() => typeof sessionStorage !== 'undefined' && sessionStorage.getItem('i18nextLng') || 'en');
  const {
    showTranslated,
    toggleTranslatedView
  } = useTranslationStyles();
  const toggleEventsNumber = useCallback(() => setEventsNumberVisible(!eventsNumberVisible), [eventsNumberVisible, setEventsNumberVisible]);
  const groupedEvents = useMemo(() => groupBy(events, 'source'), [events]);
  const filteredEvents = useMemo(() => groupedEvents[activeTab] || [], [activeTab, groupedEvents]);
  const handleTabChange = useCallback((tab: TpSource) => setActiveTab(tab), []);
  const handleVariantChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveVariant(event.target.value);
  }, []);
  const handleLanguageChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const language = event.target.value;
    setActiveLanguage(language);
    sessionStorage.setItem('i18nextLng', language);
    window.location.reload();
  }, []);
  const sortedVariants = useMemo(() => [...ALL_VARIANTS].sort((a, b) => a.variant_name.localeCompare(b.variant_name)), []);
  const categorizedVariants = useMemo(() => {
    const variantsByCategory: {
      variant: typeof sortedVariants;
      mewing: typeof sortedVariants;
      others: typeof sortedVariants;
    } = {
      variant: [],
      mewing: [],
      others: []
    };
    return sortedVariants.reduce((acc, variant) => {
      const name = variant.variant_name.toLowerCase();
      if (name.startsWith('variant')) {
        acc.variant.push(variant);
      } else if (name.includes('mewing')) {
        acc.mewing.push(variant);
      } else {
        acc.others.push(variant);
      }
      return acc;
    }, variantsByCategory);
  }, [sortedVariants]);
  const abTestDisplay = useMemo(() => abTests && abTests.length > 0 ? <div className={styles.abTestSection}>
          <h3 className={styles.sectionTitle}>Active A/B Tests:</h3>
          {abTests.map((test, index) => <p key={index}>{`${test.test_name} : ${test.case_name}`}</p>)}
        </div> : null, [abTests]);
  useEffect(() => {
    setActiveVariant(variantFromStorage);
  }, [variantFromStorage]);
  const renderEvents = useMemo(() => filteredEvents.map(({
    name,
    date,
    params
  }: Event) => <div key={`${name}-${date}`} className={styles.eventItem}>
          <span className={styles.eventName}>{name}</span>
          {params && <span className={styles.eventParams}>
              {JSON.stringify(params, null, 2)}
            </span>}
          <span className={styles.eventDate}>
            {new Date(date).toISOString()}
          </span>
        </div>), [filteredEvents]);
  return <div className={classNames(styles.host, {
    [styles.expanded]: eventsNumberVisible
  })} aria-hidden="true">
      <div className={styles.row}>
        <UiKitButton className={styles.smallButton} onClick={toggleEventsNumber}>
          {eventsNumberVisible ? 'off' : 'on'}
        </UiKitButton>

        {eventsNumberVisible && <>
            {<select className={styles.select} name="variant-select" id="select" onChange={handleVariantChange} value={sortedVariants.some(({
          variant_name
        }) => variant_name === activeVariant) ? activeVariant : 'default'}>
                <optgroup label="Variant">
                  {categorizedVariants.variant.map(({
              variant_name
            }) => <option key={variant_name} value={variant_name}>
                      {variant_name}
                    </option>)}
                </optgroup>
                <optgroup label="Mewing">
                  {categorizedVariants.mewing.map(({
              variant_name
            }) => <option key={variant_name} value={variant_name}>
                      {variant_name}
                    </option>)}
                </optgroup>
                <optgroup label="Others">
                  {categorizedVariants.others.map(({
              variant_name
            }) => <option key={variant_name} value={variant_name}>
                      {variant_name}
                    </option>)}
                </optgroup>
              </select>}
            {<select className={styles.select} name="language-select" onChange={handleLanguageChange} value={activeLanguage}>
                {Object.keys(LANG_TO_LOCALE).map(lang => <option key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </option>)}
              </select>}
            <UiKitButton className={styles.smallButton} onClick={() => window.location.replace(`quiz?hard_reset=1&variant=${activeVariant}`)}>
              Reset
            </UiKitButton>
            <UiKitButton className={styles.smallButton} onClick={clearEvents}>
              Clear
            </UiKitButton>
            {DEBUG_EVENT_SOURCES.map(key => <UiKitButton key={key} className={styles.smallButton} style={{
          background: activeTab === key ? '#0e0e0d' : '#356b61'
        }} onClick={() => handleTabChange(key as TpSource)}>
                {key}
              </UiKitButton>)}
            <UiKitButton className={styles.smallButton} onClick={toggleTranslatedView}>
              {showTranslated ? 'Hide' : 'Show'} Translated
            </UiKitButton>
          </>}
      </div>

      {eventsNumberVisible && <>
          <div className={styles.featuresRow}>
            <h3 className={styles.sectionTitle}>Active features:</h3>
            <pre className={styles.code}>
              {JSON.stringify(features, null, 2)}
            </pre>
          </div>
          {abTestDisplay}
          <div>
            <h3 className={styles.sectionTitle}>Events:</h3>
            <div className={styles.eventsSection}>{renderEvents}</div>
          </div>
        </>}
    </div>;
});